import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { BackendService, ObjectBaseService, RequestOptionsArgs, TrackingService } from '@hub-components';

import { Audit } from 'app/apps/dm/app/models/audit.model';
import { DiscoveriesResponse } from '../../models/discoveries-response.model';
import { Discovery, DiscoveryJson } from '../../models/discovery.model';

@Injectable({
  providedIn: 'root',
})
export class DiscoveriesService extends ObjectBaseService {
  schemas: { [key: string]: any } = {};

  constructor(
    private backendService: BackendService,
    private trackingService: TrackingService,
  ) {
    super();
  }

  list(options: RequestOptionsArgs = {}): Observable<DiscoveriesResponse> {
    return this.backendService.load('discoveries', options)
      .pipe(
        map((res: { discoveries: DiscoveryJson[], discoveries_count: number }) => {
          return {
            discoveries: res.discoveries.map(d => new Discovery(d)),
            discoveriesCount: res.discoveries_count,
          };
        }),
      );
  }

  listMyDiscoveries(options: RequestOptionsArgs = {}): Observable<DiscoveriesResponse> {
    this.trackingService.track('list my discoveries');
    return this.backendService.load('discoveries/my_discoveries', options)
      .pipe(
        map((res: { discoveries: DiscoveryJson[], discoveries_count: number }) => {
          return {
            discoveries: res.discoveries.map(d => new Discovery(d)),
            discoveriesCount: res.discoveries_count,
          };
        }),
      );
  }

  listSharedDiscoveries(options: RequestOptionsArgs = {}): Observable<DiscoveriesResponse> {
    return this.backendService.load('discoveries/shared_discoveries', options)
      .pipe(
        map((res: { discoveries: DiscoveryJson[], discoveries_count: number }) => {
          return {
            discoveries: res.discoveries.map(d => new Discovery(d)),
            discoveriesCount: res.discoveries_count,
          };
        }),
      );
  }

  load(discoveryId: string): Observable<Discovery> {
    this.trackingService.track('view discovery');
    return this.backendService.load(`discoveries/${discoveryId}`, { loaderText: 'Loading Discovery', throwErrors: true })
      .pipe(
        map(res => new Discovery(res)),
      );
  }

  loadSharedDiscovery(shareId: string) {
    this.trackingService.track('view shared discovery');
    return this.backendService.load(`shared_discoveries/${shareId}`, { loaderText: 'Loading Discovery', throwErrors: true }).pipe(map(res => new Discovery(res)));
  }

  loadPublicDiscovery(uuid: string) {
    this.trackingService.track('view public discovery');
    return this.backendService.load(`s/${uuid}/show`, { loaderText: 'Loading Discovery', throwErrors: true }).pipe(map(res => new Discovery(res)));
  }

  changeOwner(discoveryId, newOwner) {
    return this.backendService.update(`/discoveries/${discoveryId}/change_owner`, { owner: newOwner });
  }

  create(discovery: Discovery): Observable<Discovery> {
    return this.backendService.create('discoveries', discovery.asJson())
      .pipe(
        map(res => new Discovery(res)),
      );
  }

  update(discovery: Discovery): Observable<Discovery> {
    return this.backendService.update(`discoveries/${discovery.id}`, discovery.asJson())
      .pipe(
        map(res => new Discovery(res)),
      );
  }

  delete(discovery: Discovery) {
    return this.backendService.delete(`discoveries/${discovery.id}`)
      .pipe(
        map(res => {
          return res;
        }),
      );
  }

  new(): Observable<Discovery> {
    return of(new Discovery({
      name: 'New Discovery',
    }));
  }

  accountUsers() {
    return this.backendService.load('/account_users', { skipLoader: true });
  }

  appUsers() {
    return this.backendService.load('/app_users', { skipLoader: true, cache: true });
  }

  appSuites() {
    return this.backendService.load('/app_suites', { skipLoader: true, cache: true });
  }

  public getAudits(offset: number, limit: number, discoveryId?: string): Observable<{ audits: Audit[], total: number }> {
    const params: any = { offset, limit };
    
    let url = '/discoveries/audits';
    if (discoveryId) {
      url = `/discoveries/${discoveryId}/audits`;
    }
    
    return this.backendService.load(url, { skipLoader: true, params })
      .pipe(
        map(response => (
          {
            audits: response.audits.map(audit => new Audit(audit)),
            total: response.total,
          })),
      );
  }

  public restore(id: string) {
    return this.backendService.create(`/discoveries/audits/${id}/restore`, {});
  }
}
